// CfoInsightsDashboard.tsx
// CFO Insights Dashboard - Premium Feature
// Dark theme aligned with CFO-in-a-Box branding

import React, { useState, useEffect } from "react";
import { auth } from "../../firebase";
import { cfoInsightsAPI } from "../../services/api";
import {
  fmtDollars,
  formatNetCashFlow,
  formatProjectedCashOut,
  recommendPayrollBridge,
} from "./cfoInsightsPresentation";


// ── Tab definitions ──────────────────────────────────────────────────────────

type TabId =
  | "summary"
  | "cashflow"
  | "margin"
  | "arap"
  | "bankability"
  | "capitalstack"
  | "aging"
  | "readiness";

const tabs: { id: TabId; label: string }[] = [
  { id: "summary",      label: "Executive Summary" },
  { id: "cashflow",     label: "Cash Flow" },
  { id: "margin",       label: "Margin & COS" },
  { id: "arap",         label: "AR / AP Risk" },
  { id: "bankability",  label: "Bankability" },
  { id: "capitalstack", label: "CapitalStack" },
  { id: "aging",        label: "Aging Detail" },
  { id: "readiness",    label: "Readiness" },
];

// ── API response types ───────────────────────────────────────────────────────

interface SummaryData {
  constructScore: number;
  constructScoreBand: string;
  liquidityScore: number;
  profitabilityScore: number;
  profitabilityScoreStatus: string;
  cashFlowHealthScore: number;
  leverageScore: number | null;
  leverageScoreStatus: string;
  operationalRiskScore: number;
  bankabilityScore: number;
  bankabilityScoreStatus: string;
  financialHealthSnapshot: string;
  asOfDate: string | null;
}

interface ArApData {
  totalAr: number | null;
  totalArStatus?: string;
  arOver60Days: number | null;
  arOver60DaysStatus?: string;
  retentionReceivable: number | null;
  retentionReceivableStatus?: string;
  allowanceForBadDebt: number;
  totalAp: number | null;
  totalApStatus?: string;
  apRetention: number;
  accruedUnionWages: number;
  accruedNonUnionWages: number;
  taxesAndPayrollWithholdings: number;
  arAsOfDate?: string | null;
  financialsAsOfDate?: string | null;
}

interface AtRiskInvoice {
  customer: string;
  amount: number;
  daysLate: number;
  risk: "High" | "Medium" | "Low";
}

interface CashFlowData {
  projectedCashIn30Days: number | null;
  projectedCashIn30DaysStatus: string;
  projectedCashOut30Days: number | null;
  projectedCashOut30DaysStatus: string;
  netCashFlow30Days: number | null;
  atRiskInvoices: AtRiskInvoice[];
  keyDrivers: string[];
}

interface InvoiceAging {
  customer: string;
  jobNumber: string;
  projectManager: string;
  generalContractor: string;
  invoiceNumber: string;
  amount: number;
  agingBucket: string;
  constructpayRiskTier: number;
  lateProbability: number;
}

interface CustomerRollup {
  current: number;
  days30: number;
  days60: number;
  days90Plus: number;
  total: number;
  weightedLatePercent: number;
}

interface AgingData {
  invoices: InvoiceAging[];
  customerRollup: Record<string, CustomerRollup>;
}

interface CostOfSalesMixItem {
  category: string;
  amount: number;
}
interface OverheadItemData {
  category: string;
  amount: number;
  percentOfRevenue: number | null;
}

interface MarginData {
  revenueYtd: number | null;
  revenueYtdStatus: string;
  netIncomeYtd: number | null;
  netIncomeYtdStatus: string;
  costOfSalesYtd: number | null;
  costOfSalesYtdStatus: string;
  grossMarginYtd: number | null;
  grossMarginYtdStatus: string;
  grossMarginPercent: number | null;
  grossMarginPercentStatus: string;
  costOfSalesMix: CostOfSalesMixItem[] | null;
  overheadItems: OverheadItemData[] | null;
  wipBacklog: number | null;
  wipBacklogStatus: string;
  wipBasedGrossMargin: number | null;
  wipBasedGrossMarginStatus: string;
  wipBasedGrossMarginPercent: number | null;
  wipBasedGrossMarginPercentStatus: string;
  asOfDate: string | null;
}

interface BankabilityData {
  quickRatio: number | null;
  quickRatioStatus: string;
  debtToEquity: number | null;
  debtToEquityStatus: string;
  estimatedDscr: number | null;
  estimatedDscrStatus: string;
  currentRatio: number | null;
  currentRatioStatus: string;
  workingCapital: number | null;
  workingCapitalStatus: string;
  asOfDate: string | null;
  lenderSuretyReadout: string | null;
}

// Readiness Timeline — snapshot entry returned by GET /api/cfo-insights/snapshots
interface SnapshotEntry {
  snapshotDate: string;         // ISO date string e.g. "2026-05-31"
  cashOnHand: number | null;
  workingCapital: number | null;
  workingCapitalStatus: string;
  grossMarginPctYtd: number | null;
  grossMarginPctYtdStatus: string;
  creditAvailable: number | null;
  creditAvailableStatus: string;
  currentRatio: number | null;
  currentRatioStatus: string;
  debtToEquity: number | null;
  debtToEquityStatus: string;
  daysCashOnHand: number | null;
  daysCashOnHandStatus: string;
}

// ── Formatters ───────────────────────────────────────────────────────────────
// Dollar formatting lives in cfoInsightsPresentation (null must not become $0.00).

/** Returns a user-facing hint string for a backend status field.
 *  Returns undefined when status is "calculated" (no hint needed). */
function statusHint(status: string): string | undefined {
  switch (status) {
    case "calculated":      return undefined;
    case "policy_required": return "Policy review required — contact ConstructPay.";
    case "source_missing":  return "Source document required.";
    case "partial":         return "Partial data available.";
    case "unavailable":     return "Not available for this period.";
    default:                return undefined;
  }
}

// ── Main component ───────────────────────────────────────────────────────────

/**
 * Reads the `?tab=<id>` query param the shared shell nav uses when linking
 * back from a sibling route (e.g. Scenario Modeling's "Executive Summary"
 * link navigates to `/cfo-insights?tab=summary`). Falls back to "summary"
 * for an absent or unrecognized value so navigation context is preserved
 * instead of always landing on the default tab regardless of the link.
 */
function readInitialTabFromLocation(): TabId {
  try {
    const requested = new URLSearchParams(window.location.search).get("tab");
    if (requested && tabs.some((t) => t.id === requested)) {
      return requested as TabId;
    }
  } catch {
    // window/URLSearchParams unavailable (e.g. SSR) - fall through to default.
  }
  return "summary";
}

export function CfoInsightsDashboard() {
  const [activeTab,       setActiveTabState]  = useState<TabId>(readInitialTabFromLocation);

  // Keeps the `?tab=` query param in sync with the active tab so a link that
  // deep-links here (e.g. Scenario Modeling's "Executive Summary" / "Readiness"
  // nav items) doesn't leave a stale query string once the user clicks to a
  // different tab in-page. Uses replaceState (no new history entry, no
  // remount) since this component reads the query string directly rather
  // than through react-router hooks.
  const setActiveTab = (id: TabId) => {
    setActiveTabState(id);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", id);
      window.history.replaceState(null, "", url.toString());
    } catch {
      // window/URL unavailable - tab still switches via state, only the
      // query string sync is skipped.
    }
  };
  const [summaryData,     setSummaryData]     = useState<SummaryData     | null>(null);
  const [arapData,        setArapData]        = useState<ArApData         | null>(null);
  const [cashflowData,    setCashflowData]    = useState<CashFlowData     | null>(null);
  const [agingData,       setAgingData]       = useState<AgingData        | null>(null);
  const [marginData,      setMarginData]      = useState<MarginData       | null>(null);
  const [bankabilityData, setBankabilityData] = useState<BankabilityData  | null>(null);
  const [snapshotData,    setSnapshotData]    = useState<SnapshotEntry[]  | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError,   setSnapshotError]   = useState<string | null>(null);
  const snapshotFetched = React.useRef(false);
  const [loading,         setLoading]         = useState(false);
  const [fetchError,      setFetchError]      = useState<string | null>(null);

  // ── Main data load ───────────────────────────────────────────────────────────
  // getSnapshots is intentionally excluded so a /snapshots failure cannot
  // affect Summary, Cash Flow, Margin, AR/AP, Bankability, CapitalStack, or Aging.
  // Backend resolves org from the authenticated Firebase Bearer token.
  useEffect(() => {
    setLoading(true);
    setFetchError(null);

    Promise.all([
      cfoInsightsAPI.getSummary(),
      cfoInsightsAPI.getArAp(),
      cfoInsightsAPI.getCashFlow(),
      cfoInsightsAPI.getAging(),
      cfoInsightsAPI.getMargin(),
      cfoInsightsAPI.getBankability(),
    ])
      .then(([s, a, c, ag, m, b]) => {
        setSummaryData(s.data);
        setArapData(a.data);
        setCashflowData(c.data);
        setAgingData(ag.data);
        setMarginData(m.data);
        setBankabilityData(b.data);
      })
      .catch(err => setFetchError(err.message ?? "Unknown error"))
      .finally(() => setLoading(false));
  }, []);

  // ── Snapshots load ───────────────────────────────────────────────────────────
  // Errors are isolated: they show inside ReadinessPanel only.
  useEffect(() => {
    if (snapshotFetched.current) return;
    snapshotFetched.current = true;
    setSnapshotLoading(true);
    setSnapshotError(null);

    cfoInsightsAPI.getSnapshots()
      .then((res: { data: { snapshots?: SnapshotEntry[] } }) => {
        setSnapshotData(res.data.snapshots ?? []);
      })
      .catch((err: Error) => {
        setSnapshotError(err.message ?? "Failed to load balance sheet history.");
        setSnapshotData([]);
      })
      .finally(() => setSnapshotLoading(false));
  }, []); // load on mount so KPI strip DCOH is available on all tabs

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400 text-sm">
        Loading CFO Insights…
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-rose-400 text-sm">
        Failed to load CFO Insights: {fetchError}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      {/* Top Nav */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/10 border border-sky-500/30">
              <span className="text-lg font-bold text-sky-300">C</span>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-sky-400">
                CFO Insights
              </div>
              <div className="text-sm font-semibold text-slate-100">
                powered by ConstructPay
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-emerald-500/10 px-2 py-1 text-emerald-300 text-xs">
              Sub View
            </span>
            <span className="rounded-full bg-slate-800 px-2 py-1 text-slate-300 text-xs">
              GC View
            </span>
            <span className="rounded-full bg-slate-800 px-2 py-1 text-slate-300 text-xs">
              Lender View
            </span>
            <button
              onClick={() => auth.signOut()}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Layout */}
      <main className="mx-auto flex max-w-6xl gap-4 px-4 py-4">
        {/* Left Tabs */}
        <aside className="w-56 shrink-0 rounded-xl border border-slate-800 bg-slate-900/60 p-2">
          <div className="mb-2 px-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Dashboard
          </div>
          <nav className="space-y-1 text-sm">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition ${
                  activeTab === tab.id
                    ? "bg-sky-600 text-white shadow-sm"
                    : "text-slate-300 hover:bg-slate-800"
                }`}
              >
                <span>{tab.label}</span>
                {activeTab === tab.id && (
                  <span className="text-[10px] uppercase tracking-[0.2em] text-sky-100">
                    Active
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Right Content */}
        <section className="flex-1 space-y-4">
          <KpiStrip arap={arapData} bankability={bankabilityData} margin={marginData} latestSnapshot={snapshotData?.[0] ?? null} />

          <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
            {activeTab === "summary"      && <SummaryPanel data={summaryData} bankability={bankabilityData} margin={marginData} />}
            {activeTab === "cashflow"     && <CashFlowPanel     data={cashflowData} />}
            {activeTab === "margin"       && <MarginPanel       data={marginData} />}
            {activeTab === "arap"         && <ArApPanel         data={arapData} />}
            {activeTab === "bankability"  && <BankabilityPanel  data={bankabilityData} />}
            {activeTab === "capitalstack" && <CapitalStackPanel bankability={bankabilityData} arap={arapData} latestSnapshot={snapshotData?.[0] ?? null} margin={marginData} />}
            {activeTab === "aging"        && <AgingPanel        data={agingData} />}
            {activeTab === "readiness"    && <ReadinessPanel    data={snapshotData} loading={snapshotLoading} error={snapshotError} />}
          </div>
        </section>
      </main>
    </div>
  );
}

/* ─────────────────────────────── Shared ──────────────────────────────────── */

function KpiStrip({
  arap,
  bankability,
  margin,
  latestSnapshot,
}: {
  arap: ArApData | null;
  bankability: BankabilityData | null;
  margin: MarginData | null;
  latestSnapshot: SnapshotEntry | null;
}) {
  const deToEquityTone = (v: number | null): "good" | "warning" | "bad" | "neutral" => {
    if (v == null) return "neutral";
    if (v < 0) return "bad";       // negative equity — structurally concerning
    if (v > 2.5) return "bad";     // over-leveraged
    if (v > 1.5) return "warning";
    return "neutral";
  };

  const kpis = [
    {
      label: "Days Cash on Hand",
      value: latestSnapshot?.daysCashOnHandStatus === "calculated" && latestSnapshot.daysCashOnHand != null
        ? `${Math.round(latestSnapshot.daysCashOnHand)} days`
        : latestSnapshot != null
        ? "Source Data Pending"
        : "—",
      tone: "neutral" as const,
    },
    {
      label: "Current Ratio",
      value: bankability?.currentRatio != null ? bankability.currentRatio.toFixed(2) : "—",
      tone: "neutral" as const,
    },
    {
      label: "Working Capital",
      value: bankability?.workingCapital != null ? fmtDollars(bankability.workingCapital) : "—",
      tone: (bankability?.workingCapital != null && bankability.workingCapital < 0 ? "bad" : "neutral") as "good" | "warning" | "bad" | "neutral",
    },
    {
      label: "Gross Margin YTD",
      value: margin?.grossMarginPercent != null ? `${margin.grossMarginPercent.toFixed(2)}%` : "—",
      tone: "neutral" as const,
    },
    {
      label: "Debt / Equity",
      value: bankability?.debtToEquity != null ? bankability.debtToEquity.toFixed(2) : "—",
      tone: deToEquityTone(bankability?.debtToEquity ?? null),
    },
    {
      label: "AR > 60 Days",
      value: arap?.arOver60DaysStatus === "calculated" && arap.arOver60Days != null
        ? fmtDollars(arap.arOver60Days)
        : arap != null
        ? "Source Data Pending"
        : "—",
      tone: (arap?.arOver60DaysStatus === "calculated" && arap.arOver60Days != null && arap.arOver60Days > 0
        ? "bad"
        : "neutral") as "good" | "warning" | "bad" | "neutral",
    },
    {
      label: "Total AP",
      value: arap?.totalAp != null ? fmtDollars(arap.totalAp) : "—",
      tone: "neutral" as const,
    },
  ];

  return (
    <div className="grid gap-2 md:grid-cols-4 lg:grid-cols-7">
      {kpis.map((kpi) => (
        <div
          key={kpi.label}
          className="rounded-lg border border-slate-800 bg-slate-900/80 px-3 py-2"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
            {kpi.label}
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-50">
            {kpi.value}
          </div>
          <div className="mt-1 text-[10px] font-medium">
            {kpi.tone === "good"    && <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-300">Healthy</span>}
            {kpi.tone === "warning" && <span className="rounded-full bg-amber-500/10  px-2 py-0.5 text-amber-300">Monitor</span>}
            {kpi.tone === "bad"     && <span className="rounded-full bg-rose-500/10   px-2 py-0.5 text-rose-300">Attention</span>}
            {kpi.tone === "neutral" && <span className="rounded-full bg-slate-600/20  px-2 py-0.5 text-slate-200">Neutral</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionTitle(props: { title: string; subtitle?: string }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-2">
      <div>
        <h2 className="text-sm font-semibold text-slate-50">{props.title}</h2>
        {props.subtitle && (
          <p className="text-xs text-slate-400">{props.subtitle}</p>
        )}
      </div>
    </div>
  );
}

function CardStat(props: {
  label: string;
  value: string;
  tone?: "good" | "warning" | "bad" | "neutral";
  hint?: string;
  pillLabel?: string;
}) {
  const tone = props.tone ?? "neutral";
  const pillClass =
    tone === "good"    ? "bg-emerald-500/10 text-emerald-300" :
    tone === "warning" ? "bg-amber-500/10   text-amber-300"   :
    tone === "bad"     ? "bg-rose-500/10    text-rose-300"    :
                         "bg-slate-700/40   text-slate-200";

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-300">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
        {props.label}
      </div>
      <div className="mt-1 text-sm font-semibold text-slate-50">{props.value}</div>
      <div className="mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium">
        <span className={pillClass}>
          {tone === "good" ? "Healthy" : tone === "warning" ? "Monitor" : tone === "bad" ? "Attention" : (props.pillLabel ?? "Info")}
        </span>
      </div>
      {props.hint && <p className="mt-1 text-[11px] text-slate-400">{props.hint}</p>}
    </div>
  );
}

function MetricPill(props: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-md bg-slate-900/70 px-2 py-1">
      <span className="text-[11px] text-slate-300">{props.label}</span>
      <span className="text-xs font-semibold text-sky-300">{props.value}</span>
    </div>
  );
}

/* ─────────────────────────────── Panels ─────────────────────────────────── */

export function SummaryPanel({ data, bankability, margin }: {
  data: SummaryData | null;
  bankability?: BankabilityData | null;
  margin?: MarginData | null;
}) {
  if (!data) return <p className="text-xs text-slate-400">No summary data available.</p>;

  // Show divergence note when stored band is WATCH or better
  // but the AR aging snapshot signals elevated risk.
  const showDivergence =
    ["STRONG", "STABLE", "WATCH"].includes(data.constructScoreBand) &&
    data.financialHealthSnapshot.toLowerCase().startsWith("elevated ar risk");

  // Accepted 6c33312 / 04d469de customer-facing Executive Summary: KPI-sourced
  // bullets only. ConstructScore remains in the API payload but is not rendered.
  const liquidityBullet = bankability?.currentRatio != null
    ? bankability.currentRatio >= 1.5
      ? `Strong liquidity with a current ratio of ${bankability.currentRatio.toFixed(2)}x${bankability.workingCapital != null && bankability.workingCapital > 0 ? " and positive working capital" : ""}.`
      : bankability.currentRatio >= 1.0
      ? `Adequate liquidity, with a current ratio of ${bankability.currentRatio.toFixed(2)}x.`
      : `Liquidity is tight, with a current ratio of ${bankability.currentRatio.toFixed(2)}x.`
    : null;
  const marginBullet = margin?.grossMarginPercent != null
    ? `Gross margin of ${margin.grossMarginPercent.toFixed(1)}% YTD, based on the latest financial snapshot.`
    : null;
  const arBullet = data.financialHealthSnapshot && data.financialHealthSnapshot !== "No AR data available."
    ? data.financialHealthSnapshot
    : null;
  const leverageBullet = bankability?.debtToEquity != null
    ? `Debt-to-equity of ${bankability.debtToEquity.toFixed(2)}x — overhead and leverage detail available in the Bankability and Margin & COS views.`
    : null;
  const healthBullets = [liquidityBullet, marginBullet, arBullet, leverageBullet].filter(
    (b): b is string => b != null
  );

  return (
    <>
      <SectionTitle
        title="Executive Summary"
        subtitle="High-level health indicators based on latest financial data."
      />
      <div className="grid gap-4">
        <div className="space-y-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            Financial Health Snapshot
          </h3>
          {healthBullets.length > 0 ? (
            <ul className="list-disc space-y-1 pl-4 text-xs text-slate-300">
              {healthBullets.map((bullet, i) => (
                <li key={i}>{bullet}</li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">No summary data available.</p>
          )}
          {showDivergence && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
              Your AR aging is pulling below your overall score. Improving
              collections on older receivables is the fastest path to
              strengthening the profile.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function CashFlowPanel({ data }: { data: CashFlowData | null }) {
  if (!data) return <p className="text-xs text-slate-400">No cash flow data available.</p>;

  const netTone: "good" | "bad" | "neutral" =
    data.netCashFlow30Days == null ? "neutral" :
    data.netCashFlow30Days >= 0   ? "good"    : "bad";
  const cashOut = formatProjectedCashOut(
    data.projectedCashOut30Days,
    data.projectedCashOut30DaysStatus
  );

  return (
    <>
      <SectionTitle
        title="Cash Flow Outlook"
        subtitle="Projected 30-day cash position based on AR, AP, payroll, and retention."
      />
      <div className="grid gap-4 md:grid-cols-3">
        <CardStat
          label="Projected Cash In (30 days)"
          value={data.projectedCashIn30Days != null ? fmtDollars(data.projectedCashIn30Days) : "—"}
          hint={data.projectedCashIn30DaysStatus !== "calculated"
            ? statusHint(data.projectedCashIn30DaysStatus)
            : "AR due or current within 30 days."}
        />
        <CardStat
          label="Projected Cash Out (30 days)"
          value={cashOut.value}
          hint={cashOut.hint}
        />
        <CardStat
          label="Net Cash Flow (30 days)"
          value={formatNetCashFlow(data.netCashFlow30Days)}
          tone={netTone}
        />
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Top At-Risk Invoices
          </h3>
          <table className="w-full border-collapse text-xs">
            <thead className="text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-1 text-left">Customer</th>
                <th className="py-1 text-right">Amount</th>
                <th className="py-1 text-right">Days Late</th>
                <th className="py-1 text-right">Risk</th>
              </tr>
            </thead>
            <tbody>
              {data.atRiskInvoices.slice(0, 5).map((inv, i) => {
                const rc =
                  inv.risk === "High"   ? "text-rose-300"    :
                  inv.risk === "Medium" ? "text-amber-300"   :
                                          "text-emerald-300";
                return (
                  <tr key={i} className="border-b border-slate-900">
                    <td className="py-1 pr-2 text-slate-200">{inv.customer}</td>
                    <td className="py-1 text-right">{fmtDollars(inv.amount)}</td>
                    <td className="py-1 text-right">{inv.daysLate}</td>
                    <td className={`py-1 text-right ${rc}`}>{inv.risk}</td>
                  </tr>
                );
              })}
              {data.atRiskInvoices.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-2 text-[11px] text-slate-500">
                    No at-risk invoices.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Key Drivers
          </h3>
          <ul className="space-y-2 text-xs text-slate-300">
            {data.keyDrivers.map((d, i) => (
              <li key={i}>• {d}</li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

export function MarginPanel({ data }: { data: MarginData | null }) {
  if (!data) return (
    <>
      <SectionTitle
        title="Margin & Cost of Sales"
        subtitle="Composition of cost of sales and gross margin drivers."
      />
      <p className="text-xs text-slate-400">No margin data available.</p>
    </>
  );

  return (
    <>
      <SectionTitle
        title="Margin & Cost of Sales"
        subtitle="Composition of cost of sales and gross margin drivers."
      />

      {/* P&L metrics — source-backed where available */}
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
        P&amp;L Metrics
      </h3>
      <div className="grid gap-4 md:grid-cols-3 mb-4">
        <CardStat
          label="Revenue YTD"
          value={data.revenueYtd != null ? fmtDollars(data.revenueYtd) : "—"}
          tone={data.revenueYtd != null ? "neutral" : "neutral"}
          hint={data.revenueYtdStatus !== "calculated" ? "Source pending." : undefined}
          pillLabel="Actual"
        />
        {(() => {
          const overhead = data.overheadItems?.[0]?.amount ?? null;
          const gmDerived = (data.revenueYtd != null && data.costOfSalesYtd != null)
            ? data.revenueYtd - data.costOfSalesYtd : null;
          const opIncomeProxy = (gmDerived != null && overhead != null)
            ? gmDerived - overhead : null;
          return (
            <CardStat
              label="Operating Income Proxy*"
              value={data.netIncomeYtd != null
                ? fmtDollars(data.netIncomeYtd)
                : opIncomeProxy != null
                ? fmtDollars(opIncomeProxy)
                : "—"}
              tone={opIncomeProxy != null ? (opIncomeProxy >= 0 ? "neutral" : "warning") : "neutral"}
              hint={data.netIncomeYtdStatus !== "calculated"
                ? "* ConstructPay proxy: Revenue YTD − COS YTD − Operating Expenses. Not a P&L substitute."
                : undefined}
              pillLabel="Proxy"
            />
          );
        })()}
        <CardStat
          label="Cost of Sales YTD"
          value={data.costOfSalesYtd != null ? fmtDollars(data.costOfSalesYtd) : "—"}
          hint={statusHint(data.costOfSalesYtdStatus)}
          pillLabel="Actual"
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2 mb-4">
        {(() => {
          const gmDerived = (data.revenueYtd != null && data.costOfSalesYtd != null)
            ? data.revenueYtd - data.costOfSalesYtd : null;
          return (
            <CardStat
              label={data.grossMarginYtd != null ? "Gross Margin YTD" : "Gross Margin YTD*"}
              value={data.grossMarginYtd != null
                ? fmtDollars(data.grossMarginYtd)
                : gmDerived != null
                ? fmtDollars(gmDerived)
                : "—"}
              hint={data.grossMarginYtd == null && gmDerived != null
                ? "* Derived: Revenue YTD − Cost of Sales YTD."
                : statusHint(data.grossMarginYtdStatus)}
              pillLabel="Calculated"
            />
          );
        })()}
        <CardStat
          label="Gross Margin %"
          value={data.grossMarginPercent != null ? `${data.grossMarginPercent.toFixed(2)}%` : "—"}
          hint={statusHint(data.grossMarginPercentStatus)}
          pillLabel="Calculated"
        />
      </div>

      {/* WIP-based proxy metrics */}
      <h3 className="mt-2 mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
        WIP-Based Proxy (Project-Level)
      </h3>
      <div className="grid gap-4 md:grid-cols-3">
        <CardStat
          label="WIP Backlog"
          value={data.wipBacklog != null ? fmtDollars(data.wipBacklog) : "—"}
          tone={data.wipBacklog != null ? "good" : "neutral"}
          hint={data.wipBacklogStatus !== "calculated" ? "Requires WIP schedule." : undefined}
        />
        <CardStat
          label="WIP Gross Margin"
          value={data.wipBasedGrossMargin != null ? fmtDollars(data.wipBasedGrossMargin) : "—"}
          tone={data.wipBasedGrossMargin != null ? "good" : "neutral"}
          hint={data.wipBasedGrossMarginStatus !== "calculated" ? "Requires WIP schedule." : undefined}
        />
        <CardStat
          label="WIP Gross Margin %"
          value={data.wipBasedGrossMarginPercent != null ? `${data.wipBasedGrossMarginPercent.toFixed(2)}%` : "—"}
          tone={data.wipBasedGrossMarginPercent != null ? "good" : "neutral"}
          hint={data.wipBasedGrossMarginPercentStatus !== "calculated" ? "Requires WIP schedule." : undefined}
        />
      </div>
      <p className="mt-3 text-[11px] text-slate-500">
        P&amp;L metrics sourced from financial statements. WIP metrics are project-level proxies derived from the WIP schedule and are not a substitute for P&amp;L gross margin.
      </p>
    </>
  );
}

export function ArApPanel({ data }: { data: ArApData | null }) {
  if (!data) return <p className="text-xs text-slate-400">No AR/AP data available.</p>;

  const agingCurrent = data.arOver60DaysStatus === "calculated" && data.arOver60Days != null;
  const totalAr = data.totalAr;
  const totalAp = data.totalAp;
  const arOver60 = agingCurrent ? data.arOver60Days! : null;
  const arAgingPct =
    agingCurrent && totalAr != null && totalAr > 0 ? arOver60! / totalAr : null;
  const arAgingTone: "good" | "warning" | "bad" | "neutral" =
    arAgingPct == null
      ? "neutral"
      : arAgingPct > 0.40
      ? "bad"
      : arAgingPct > 0.20
      ? "warning"
      : "good";
  const netArExposure =
    agingCurrent && totalAr != null ? totalAr - arOver60! : null;

  const apAccruals: { label: string; value: number }[] = [
    { label: "AP Retention",              value: data.apRetention },
    { label: "Accrued Union Wages",       value: data.accruedUnionWages },
    { label: "Accrued Non-union Wages",   value: data.accruedNonUnionWages },
    { label: "Taxes & Payroll Withholdings", value: data.taxesAndPayrollWithholdings },
  ].filter(item => item.value > 0);

  return (
    <>
      <SectionTitle
        title="AR / AP Risk"
        subtitle="Receivables aging risk and payables pressure — liquidity impact for CFO, lender, and surety review."
      />

      <div className="grid gap-3 md:grid-cols-3 mb-4">
        <CardStat
          label="Total AR"
          value={totalAr != null ? fmtDollars(totalAr) : "—"}
          tone="neutral"
          hint={
            data.financialsAsOfDate
              ? `Gross accounts receivable outstanding as of ${data.financialsAsOfDate}.`
              : "Gross accounts receivable outstanding."
          }
        />
        <CardStat
          label="AR > 60 Days"
          value={agingCurrent ? fmtDollars(arOver60!) : "Source Data Pending"}
          tone={arAgingTone}
          hint={
            agingCurrent && arAgingPct != null
              ? `${(arAgingPct * 100).toFixed(0)}% of total AR is past 60 days. ${
                  arAgingPct > 0.40
                    ? "Elevated collection risk — lender and surety will scrutinize."
                    : arAgingPct > 0.20
                    ? "Monitor for collection slowdown."
                    : "Within acceptable range."
                }`
              : "AR aging detail is not available for the current financial period."
          }
        />
        <CardStat
          label="Current AR (≤60 Days)"
          value={netArExposure != null ? fmtDollars(netArExposure) : "Source Data Pending"}
          tone={netArExposure != null ? "good" : "neutral"}
          hint={
            netArExposure != null
              ? "Receivables expected to collect within normal payment terms."
              : "Requires same-period AR aging detail."
          }
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Receivables Risk Summary
          </h3>
          <ul className="space-y-2 text-xs text-slate-300">
            <li className="flex justify-between">
              <span>Total AR</span>
              <span className="font-semibold font-mono">{totalAr != null ? fmtDollars(totalAr) : "—"}</span>
            </li>
            <li className={`flex justify-between ${agingCurrent ? "text-rose-300" : ""}`}>
              <span>Past 60 Days</span>
              <span className="font-semibold font-mono">
                {agingCurrent ? fmtDollars(arOver60!) : "Source Data Pending"}
              </span>
            </li>
            <li className="flex justify-between border-t border-slate-800 pt-1">
              <span>Collection Risk %</span>
              <span className={`font-semibold font-mono ${arAgingTone === "bad" ? "text-rose-300" : arAgingTone === "warning" ? "text-amber-300" : "text-emerald-300"}`}>
                {arAgingPct != null ? `${(arAgingPct * 100).toFixed(1)}%` : "—"}
              </span>
            </li>
            {(data.retentionReceivable ?? 0) > 0 && (
              <li className="flex justify-between">
                <span>Retention Receivable</span>
                <span className="font-semibold font-mono">{fmtDollars(data.retentionReceivable)}</span>
              </li>
            )}
            {data.allowanceForBadDebt > 0 && (
              <li className="flex justify-between text-slate-500">
                <span>Allowance for Bad Debt</span>
                <span className="font-mono">({fmtDollars(data.allowanceForBadDebt)})</span>
              </li>
            )}
          </ul>
          <p className="mt-2 text-[10px] text-slate-500">
            {!agingCurrent
              ? "Aging metrics require invoice-level AR detail for the active financial period."
              : arAgingPct != null && arAgingPct > 0.40
              ? "High concentration of aged AR increases collection risk and reduces near-term liquidity certainty."
              : "AR aging is within normal contractor range. Monitor for concentration in any single customer."}
          </p>
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Payables &amp; Obligations Summary
          </h3>
          <ul className="space-y-2 text-xs text-slate-300">
            <li className="flex justify-between">
              <span>Accounts Payable</span>
              <span className="font-semibold font-mono">{totalAp != null ? fmtDollars(totalAp) : "—"}</span>
            </li>
            {apAccruals.map(item => (
              <li key={item.label} className="flex justify-between">
                <span>{item.label}</span>
                <span className="font-semibold font-mono">{fmtDollars(item.value)}</span>
              </li>
            ))}
            <li className="flex justify-between border-t border-slate-800 pt-1">
              <span>AR / AP Ratio</span>
              <span className={`font-semibold font-mono ${totalAr != null && totalAp != null && totalAr > totalAp ? "text-emerald-300" : "text-amber-300"}`}>
                {totalAr != null && totalAp != null && totalAp > 0 ? (totalAr / totalAp).toFixed(2) + "×" : "—"}
              </span>
            </li>
          </ul>
          <p className="mt-2 text-[10px] text-slate-500">
            {totalAr != null && totalAp != null && totalAr > totalAp
              ? `AR exceeds AP by ${fmtDollars(totalAr - totalAp)} — receivables coverage supports near-term payables obligations.`
              : "AP exceeds AR — monitor timing of collections against payment obligations."}
            {apAccruals.length === 0
              ? " No material accrued obligations identified beyond AP."
              : ""}
          </p>
        </div>
      </div>
    </>
  );
}

function BankabilityPanel({ data }: { data: BankabilityData | null }) {
  if (!data) return (
    <>
      <SectionTitle
        title="Bankability & Ratios"
        subtitle="Key metrics for lenders, sureties, and GC risk review."
      />
      <p className="text-xs text-slate-400">No bankability data available.</p>
    </>
  );

  const currentRatioTone = (r: number | null): "good" | "warning" | "bad" | "neutral" => {
    if (r == null) return "neutral";
    if (r >= 1.2) return "good";
    if (r >= 1.0) return "warning";
    return "bad";
  };

  const workingCapitalTone = (w: number | null): "good" | "warning" | "bad" | "neutral" => {
    if (w == null) return "neutral";
    return w >= 0 ? "good" : "bad";
  };

  return (
    <>
      <SectionTitle
        title="Bankability & Ratios"
        subtitle="Key metrics for lenders, sureties, and GC risk review."
      />
      <div className="grid gap-4 md:grid-cols-3">
        <CardStat
          label="Current Ratio"
          value={data.currentRatio != null ? data.currentRatio.toFixed(2) : "—"}
          tone={currentRatioTone(data.currentRatio)}
          hint={data.currentRatioStatus !== "calculated" ? "Requires balance sheet." : undefined}
        />
        <CardStat
          label="Working Capital"
          value={data.workingCapital != null ? fmtDollars(data.workingCapital) : "—"}
          tone={workingCapitalTone(data.workingCapital)}
          hint={data.workingCapitalStatus !== "calculated" ? "Requires balance sheet." : undefined}
        />
        <CardStat
          label="Quick Ratio*"
          value={data.quickRatio != null
            ? data.quickRatio.toFixed(2)
            : data.currentRatio != null
            ? (data.currentRatio * 0.85).toFixed(2) + "*"
            : "—"}
          hint={data.quickRatio == null && data.currentRatio != null
            ? "* ConstructPay proxy: Current Ratio × 0.85. GC/sub inventory is minimal — quick assets approximate current assets. Exact value requires quick asset definition."
            : statusHint(data.quickRatioStatus)}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2 mt-4">
        <CardStat
          label="Debt / Equity"
          value={data.debtToEquity != null ? data.debtToEquity.toFixed(2) : "—"}
          tone={
            data.debtToEquityStatus !== "calculated" ? "neutral" :
            data.debtToEquity == null               ? "neutral" :
            data.debtToEquity < 0                   ? "bad"     :
            data.debtToEquity > 2.5                 ? "bad"     :
            data.debtToEquity > 1.5                 ? "warning" :
            data.debtToEquity <= 1.0                ? "good"    : "neutral"
          }
          hint={
            data.debtToEquityStatus !== "calculated"
              ? statusHint(data.debtToEquityStatus)
              : data.debtToEquity != null && data.debtToEquity < 0
                ? "Negative equity — lender and surety review required."
                : undefined
          }
        />
        <CardStat
          label="Est. DSCR*"
          value={data.estimatedDscr != null
            ? data.estimatedDscr.toFixed(2)
            : (data.debtToEquity != null && data.currentRatio != null)
            ? (data.debtToEquity < 1.0 && data.currentRatio >= 2.0
                ? "≥ 1.20*"
                : data.debtToEquity < 1.5 && data.currentRatio >= 1.2
                ? "≥ 1.10*"
                : "~1.0*")
            : "—"}
          hint={data.estimatedDscr == null
            ? "* ConstructPay estimate based on D/E and liquidity profile. Exact value requires debt service schedule (debt amortization + interest)."
            : statusHint(data.estimatedDscrStatus)}
        />
      </div>
      <p className="mt-3 text-[11px] text-slate-500">
        Quick Ratio requires quick asset definition (policy). DSCR requires debt service schedule (policy review). Debt/Equity calculated from total liabilities and total equity.
      </p>
    </>
  );
}

export function CapitalStackPanel({
  bankability,
  arap,
  latestSnapshot,
  margin,
}: {
  bankability: BankabilityData | null;
  arap: ArApData | null;
  latestSnapshot: SnapshotEntry | null;
  margin: MarginData | null;
}) {
  const wc       = bankability?.workingCapital ?? null;
  const cr       = bankability?.currentRatio   ?? null;
  const de       = bankability?.debtToEquity   ?? null;
  const ar60     = arap?.arOver60Days          ?? null;
  const totalAr  = arap?.totalAr               ?? null;
  const ap       = arap?.totalAp               ?? null;
  const dcoh     = latestSnapshot?.daysCashOnHandStatus === "calculated"
    ? latestSnapshot?.daysCashOnHand ?? null : null;
  const cash     = latestSnapshot?.cashOnHand  ?? null;
  const locAvail = latestSnapshot?.creditAvailable ?? null;
  const rev      = margin?.revenueYtd          ?? null;
  const cos      = margin?.costOfSalesYtd      ?? null;

  const {
    payrollValue,
    payrollCapacity,
    payrollTone,
    payrollHint,
  } = recommendPayrollBridge({
    locAvailable: locAvail,
    ar60,
    totalAr,
    ap,
    dcoh,
    wc,
    cr,
  });

  // Materials Financing — indicated when COS intensity is high
  const cosIntensity = rev != null && cos != null && rev > 0 ? cos / rev : null;
  const materialsValue =
    cosIntensity != null && cosIntensity > 0.80 ? "Recommended" :
    cosIntensity != null && cosIntensity > 0.65 ? "Consider"    : "Not Currently Indicated";
  const materialsTone: "good" | "warning" | "neutral" =
    materialsValue === "Recommended" ? "warning" :
    materialsValue === "Consider"    ? "neutral" : "good";
  const materialsHint = cosIntensity != null
    ? `* COS intensity ${(cosIntensity * 100).toFixed(1)}% of revenue YTD. ${
        cosIntensity > 0.80
          ? "Materials pre-pay financing can reduce cash timing pressure on active jobs."
          : cosIntensity > 0.65
          ? "Moderate COS load — evaluate materials financing if job volume increases."
          : "COS intensity does not indicate a materials financing need at this time."
      }`
    : "* Revenue YTD and COS YTD required to evaluate materials financing need.";

  // ── Equipment Lease / Loan — deterministic rule-based recommendation ────────
  // Signal scoring from available profile metrics.
  // Positive signals = lease more likely beneficial (preserves liquidity / avoids debt load).
  // Negative signals = balance sheet strong enough to absorb ownership if needed.
  let equipScore = 0;
  const equipFactors: string[] = [];

  // DCOH — low runway favors leasing over cash purchase
  if (dcoh != null && dcoh < 30) {
    equipScore += 2;
    equipFactors.push(`DCOH ${Math.round(dcoh)} days — very short runway; lease preserves operating cash`);
  } else if (dcoh != null && dcoh < 45) {
    equipScore += 1;
    equipFactors.push(`DCOH ${Math.round(dcoh)} days — leasing keeps cash available for operations`);
  } else if (dcoh != null && dcoh >= 60) {
    equipScore -= 1; // strong cash runway reduces urgency to lease
  }

  // D/E — elevated leverage favors lease over additional term debt
  if (de != null && de > 1.5) {
    equipScore += 2;
    equipFactors.push(`D/E ${de.toFixed(2)} — elevated; lease avoids additional term debt`);
  } else if (de != null && de > 1.0) {
    equipScore += 1;
    equipFactors.push(`D/E ${de.toFixed(2)} — lease preferred over additional debt at this leverage`);
  } else if (de != null && de < 0.5) {
    equipScore -= 1; // very low leverage; balance sheet can absorb ownership
  }

  // Working Capital — constrained WC favors leasing to preserve liquidity
  if (wc != null && wc < 300_000) {
    equipScore += 2;
    equipFactors.push(`Working capital ${fmtDollars(wc)} — tight; equipment loan would pressure liquidity`);
  } else if (wc != null && wc < 500_000) {
    equipScore += 1;
    equipFactors.push(`Working capital ${fmtDollars(wc)} — lease preferred over upfront purchase`);
  } else if (wc != null && wc >= 750_000) {
    equipScore -= 1; // strong WC; less urgency to lease
  }

  // COS intensity — high COS may reflect equipment-intensive operations
  if (cosIntensity != null && cosIntensity > 0.80) {
    equipScore += 1;
    equipFactors.push(`COS intensity ${(cosIntensity * 100).toFixed(1)}% — equipment dependency likely`);
  }

  // LOC Available — low available credit increases leasing attractiveness
  if (locAvail != null && locAvail < 150_000) {
    equipScore += 1;
    equipFactors.push(`LOC available ${fmtDollars(locAvail)} — limited credit headroom`);
  } else if (locAvail != null && locAvail >= 500_000) {
    equipScore -= 1; // ample LOC reduces lease urgency
  }

  // Current Ratio — strong CR reduces urgency to lease
  if (cr != null && cr >= 2.5) equipScore -= 1;

  // Cash — high absolute cash reduces lease urgency
  if (cash != null && cash >= 1_000_000) equipScore -= 1;

  // Determine recommendation tier from final signal score
  const equipValue: string =
    equipScore >= 4 ? "Evaluate Lease Option"       :
    equipScore >= 2 ? "Consider if Acquiring"        :
    equipScore >= 0 ? "Low Indication"               :
                      "Not Currently Indicated";

  const equipTone: "good" | "warning" | "neutral" =
    equipScore >= 4 ? "warning" :
    equipScore >= 2 ? "neutral" : "good";

  const primaryFactor = equipFactors.length > 0
    ? equipFactors[0]
    : `D/E ${de?.toFixed(2) ?? "—"}, current ratio ${cr?.toFixed(2) ?? "—"}, and working capital ${wc != null ? fmtDollars(wc) : "—"} do not signal an equipment financing need`;
  const secondaryFactor = equipFactors.length > 1 ? ` Additional: ${equipFactors[1]}.` : "";

  const equipHint =
    `ConstructPay Recommendation — Illustrative recommendation, not lender approval. ` +
    `Signal score: ${equipScore}. ${primaryFactor}.${secondaryFactor}`;

  // Materials capacity range — derived from COS intensity and revenue run rate
  const materialsCapacity: string = (() => {
    if (cosIntensity == null || rev == null) return "Capacity TBD — connect margin data";
    const monthlyRev = rev / 5; // YTD through May = 5 months
    const monthlyMaterials = monthlyRev * cosIntensity;
    if (monthlyMaterials > 300_000) return `${fmtDollars(Math.round(monthlyMaterials / 50_000) * 50_000)} / mo est.`;
    if (monthlyMaterials > 100_000) return `${fmtDollars(Math.round(monthlyMaterials / 25_000) * 25_000)} / mo est.`;
    return `${fmtDollars(Math.round(monthlyMaterials / 10_000) * 10_000)} / mo est.`;
  })();

  // Equipment Lease Capacity — approved conservative formula
  // min(wc×0.25, locAvail×0.50, rev×0.075), with risk reductions applied before rounding
  const equipCapacity: string = (() => {
    if (wc == null || locAvail == null || rev == null) {
      return "Capacity TBD — connect balance sheet";
    }
    let base = Math.min(wc * 0.25, locAvail * 0.50, rev * 0.075);
    if (de != null && de > 1.0)   base *= 0.75;
    if (cr != null && cr < 1.5)   base *= 0.75;
    if (dcoh != null && dcoh < 45) base *= 0.75;
    const rounded = Math.round(base / 25_000) * 25_000;
    return `~${fmtDollars(rounded)}`;
  })();

  // Equipment priority tier — from signal score (retained for tone and status pill)
  const equipPriority: string =
    equipScore >= 4 ? "Priority: Evaluate Now"     :
    equipScore >= 2 ? "Priority: Plan if Acquiring" :
    equipScore >= 0 ? "Priority: Low"               :
                      "Priority: Not Indicated";

  return (
    <>
      <SectionTitle
        title="CapitalStack Recommendations"
        subtitle="ConstructPay-generated financing mix based on current financial profile. Not lender approvals."
      />
      <div className="grid gap-4 md:grid-cols-3">

        {/* Payroll Bridge LOC */}
        <div className={`rounded-lg border bg-slate-950/40 p-3 text-xs ${
          payrollTone === "warning" ? "border-amber-500/30" :
          payrollTone === "good"    ? "border-emerald-500/20" : "border-slate-800"
        }`}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">Payroll Bridge LOC</div>
          <div className="text-[10px] text-slate-500 mb-2">Use Case: Timing-gap coverage between job costs and AR collections</div>
          <div className="text-sm font-semibold text-slate-50 mb-1">{payrollCapacity}</div>
          <div className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium mb-2 ${
            payrollTone === "warning" ? "bg-amber-500/10 text-amber-300" :
            payrollTone === "good"    ? "bg-emerald-500/10 text-emerald-300" :
                                        "bg-slate-700/40 text-slate-200"
          }`}>{payrollValue}</div>
          <div className="space-y-0.5 text-[10px] text-slate-400">
            <div>• AR &gt;60 Days: <span className="text-slate-300">{ar60 != null ? fmtDollars(ar60) : "—"}</span></div>
            <div>• Total AP: <span className="text-slate-300">{ap != null ? fmtDollars(ap) : "—"}</span></div>
            <div>• Existing LOC available: <span className="text-slate-300">{locAvail != null ? fmtDollars(locAvail) : "—"}</span></div>
            <div>• DCOH: <span className="text-slate-300">{dcoh != null ? `${Math.round(dcoh)} days` : "—"}</span></div>
          </div>
          <p className="mt-2 text-[9.5px] text-slate-500">{payrollHint}</p>
          <p className="mt-1 text-[9.5px] text-slate-500">ConstructPay Recommendation — Illustrative, not lender approval.</p>
        </div>

        {/* Materials Financing */}
        <div className={`rounded-lg border bg-slate-950/40 p-3 text-xs ${
          materialsTone === "warning" ? "border-amber-500/30" :
          materialsTone === "good"    ? "border-emerald-500/20" : "border-slate-800"
        }`}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">Materials Financing</div>
          <div className="text-[10px] text-slate-500 mb-2">Use Case: Project materials timing — reduce cash tied up in pre-purchase</div>
          <div className="text-sm font-semibold text-slate-50 mb-1">{materialsCapacity}</div>
          <div className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium mb-2 ${
            materialsTone === "warning" ? "bg-amber-500/10 text-amber-300" :
            materialsTone === "good"    ? "bg-emerald-500/10 text-emerald-300" :
                                          "bg-slate-700/40 text-slate-200"
          }`}>{materialsValue}</div>
          <div className="space-y-0.5 text-[10px] text-slate-400">
            <div>• COS Intensity: <span className="text-slate-300">{cosIntensity != null ? `${(cosIntensity * 100).toFixed(1)}% of revenue` : "—"}</span></div>
            <div>• Revenue YTD: <span className="text-slate-300">{rev != null ? fmtDollars(rev) : "—"}</span></div>
            <div>• Working Capital: <span className="text-slate-300">{wc != null ? fmtDollars(wc) : "—"}</span></div>
          </div>
          <p className="mt-2 text-[9.5px] text-slate-500">ConstructPay Recommendation — Illustrative, not lender approval.</p>
        </div>

        {/* Equipment Lease / Loan */}
        <div className={`rounded-lg border bg-slate-950/40 p-3 text-xs ${
          equipTone === "warning" ? "border-amber-500/30" :
          equipTone === "good"    ? "border-emerald-500/20" : "border-slate-800"
        }`}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">Equipment Lease / Loan</div>
          <div className="text-[10px] text-slate-500 mb-2">Use Case: Equipment replacement or fleet support — ownership vs. lease decision</div>
          <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500 mb-0.5">Estimated Capacity</div>
          <div className="text-sm font-semibold text-slate-50 mb-1">{equipCapacity}</div>
          <div className="text-[9.5px] text-slate-500 mb-1">{equipPriority}</div>
          <div className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium mb-2 ${
            equipTone === "warning" ? "bg-amber-500/10 text-amber-300" :
            equipTone === "good"    ? "bg-emerald-500/10 text-emerald-300" :
                                      "bg-slate-700/40 text-slate-200"
          }`}>{equipValue}</div>
          <div className="space-y-0.5 text-[10px] text-slate-400">
            <div>• D/E: <span className="text-slate-300">{de != null ? de.toFixed(2) : "—"}</span></div>
            <div>• Current Ratio: <span className="text-slate-300">{cr != null ? cr.toFixed(2) : "—"}</span></div>
            <div>• Cash on Hand: <span className="text-slate-300">{cash != null ? fmtDollars(cash) : "—"}</span></div>
          </div>
          <p className="mt-2 text-[9.5px] text-slate-500">ConstructPay Recommendation — Illustrative, not lender approval.</p>
        </div>

      </div>
      <p className="mt-3 text-[10px] text-slate-500">
        Inputs: CR {cr?.toFixed(2) ?? "—"} · WC {wc != null ? fmtDollars(wc) : "—"} · DCOH {dcoh != null ? `${Math.round(dcoh)}d` : "—"} · D/E {de?.toFixed(2) ?? "—"} · COS {cosIntensity != null ? `${(cosIntensity * 100).toFixed(1)}%` : "—"}.
        Recommendations update automatically as source metrics change.
      </p>
    </>
  );
}

function AgingPanel({ data }: { data: AgingData | null }) {
  if (!data) return <p className="text-xs text-slate-400">No aging data available.</p>;

  const rollupEntries = Object.entries(data.customerRollup).sort(
    (a, b) => b[1].total - a[1].total
  );

  // Sum only positive balances — credit balances are excluded from the AR denominator.
  const grandTotal = Object.values(data.customerRollup).reduce(
    (sum, r) => sum + (r.total > 0 ? r.total : 0),
    0
  );

  return (
    <>
      <SectionTitle
        title="Aging Detail"
        subtitle="Invoice-level aging with customer rollups and ConstructPay risk tiers."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Invoice-Level Aging
          </h3>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full border-collapse text-[11px]">
              <thead className="sticky top-0 bg-slate-950/90 text-slate-400">
                <tr className="border-b border-slate-800">
                  <th className="py-1 text-left">Customer</th>
                  <th className="py-1 text-left">Job #</th>
                  <th className="py-1 text-left">Invoice #</th>
                  <th className="py-1 text-right">Amt</th>
                  <th className="py-1 text-right">Bucket</th>
                  <th className="py-1 text-right">Tier</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv, i) => {
                  const isCredit = inv.amount < 0;
                  const bc = isCredit
                    ? "text-slate-500"
                    : inv.agingBucket === "90+" || inv.agingBucket === "120+"
                    ? "text-rose-300"
                    : inv.agingBucket === "60"
                    ? "text-amber-300"
                    : "text-slate-300";
                  return (
                    <tr key={i} className="border-b border-slate-900">
                      <td className="py-1 pr-2 text-slate-200">{inv.customer}</td>
                      <td className="py-1 pr-2">{inv.jobNumber}</td>
                      <td className="py-1 pr-2">{inv.invoiceNumber}</td>
                      <td className={`py-1 text-right ${isCredit ? "text-slate-500" : ""}`}>
                        {fmtDollars(inv.amount)}
                      </td>
                      <td className={`py-1 text-right ${bc}`}>
                        {isCredit ? "Credit" : inv.agingBucket}
                      </td>
                      <td className="py-1 text-right">
                        {isCredit
                          ? <span className="text-slate-500">—</span>
                          : `Tier ${inv.constructpayRiskTier}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            Job, PM, and GC context helps ConstructPay tie aging risk back to
            specific projects and project teams.
          </p>
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Customer Rollup
          </h3>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full border-collapse text-[11px]">
              <thead className="sticky top-0 bg-slate-950/90 text-slate-400">
                <tr className="border-b border-slate-800">
                  <th className="py-1 text-left">Customer</th>
                  <th className="py-1 text-right">30</th>
                  <th className="py-1 text-right">60</th>
                  <th className="py-1 text-right">90+</th>
                  <th className="py-1 text-right">Total</th>
                  <th className="py-1 text-right">% of AR</th>
                </tr>
              </thead>
              <tbody>
                {rollupEntries.map(([name, r], i) => {
                  const isCredit = r.total < 0;
                  const arShare = isCredit || grandTotal === 0
                    ? "Credit"
                    : `${((r.total / grandTotal) * 100).toFixed(1)}%`;
                  return (
                    <tr key={i} className="border-b border-slate-900">
                      <td className="py-1 pr-2 text-slate-200">{name}</td>
                      <td className="py-1 text-right">{r.days30   > 0 ? fmtDollars(r.days30)   : "—"}</td>
                      <td className="py-1 text-right">{r.days60   > 0 ? fmtDollars(r.days60)   : "—"}</td>
                      <td className={`py-1 text-right ${r.days90Plus > 0 ? "text-rose-300" : ""}`}>
                        {r.days90Plus > 0 ? fmtDollars(r.days90Plus) : "—"}
                      </td>
                      <td className={`py-1 text-right font-semibold ${isCredit ? "text-slate-500" : ""}`}>
                        {fmtDollars(r.total)}
                      </td>
                      <td className={`py-1 text-right ${isCredit ? "text-slate-500" : ""}`}>
                        {arShare}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            These values are calculated from the invoice-level aging data
            and surfaced here for CFOs, GCs, and lenders.
          </p>
        </div>
      </div>
    </>
  );
}

/* ───────────────────── Readiness Timeline ────────────────────────────────── */

/**
 * Converts the DESC-ordered snapshot list from the API into a monthly series.
 * Since the list is DESC, the first occurrence of each calendar month is the
 * latest snapshot for that month — exactly what the Readiness Timeline wants.
 * Returns the series in ASC order for left-to-right display.
 *
 * February dedup: 2026-02-28 appears before 2026-02-12 in DESC order → selected ✓
 * April dedup:    2026-04-30 is the only April row → selected ✓
 */
function toMonthlySeries(snapshots: SnapshotEntry[]): SnapshotEntry[] {
  const seen = new Set<string>();
  const monthly: SnapshotEntry[] = [];
  for (const s of snapshots) {
    const monthKey = s.snapshotDate.substring(0, 7); // "2026-02"
    if (!seen.has(monthKey)) {
      seen.add(monthKey);
      monthly.push(s);
    }
  }
  return monthly.reverse(); // ASC for display
}

function fmtMonthLabel(isoDate: string): string {
  // "2025-10-31" → "Oct '25"
  const [y, m] = isoDate.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m, 10) - 1]} '${y.slice(2)}`;
}

/**
 * Historical Cash Balance — interactive SVG area/line chart.
 * Hover or click a monthly point to see a callout with month, cash, and delta vs baseline.
 * Full-width (no right-panel partner). No tooltip library — pure React state + SVG.
 */
function HistoricalCashChart({ monthly }: { monthly: SnapshotEntry[] }) {
  const [activeIdx, setActiveIdx] = React.useState<number | null>(null);

  if (monthly.length < 2) return null;
  const values = monthly.map(d => d.cashOnHand ?? 0);
  const baseline = values[0]; // Oct '25 is always index 0 after toMonthlySeries
  const W = 600, H = 240;
  const padTop = 16, padBottom = 36, padLeft = 62, padRight = 16;
  const innerW = W - padLeft - padRight;
  const innerH = H - padTop - padBottom;
  const maxV = Math.max(...values) * 1.18;
  const rng = maxV || 1;

  const toX = (i: number) => padLeft + (i / (monthly.length - 1)) * innerW;
  const toY = (v: number) => padTop + innerH - (v / rng) * innerH;

  const linePts = values.map((v, i) => `${toX(i)},${toY(v)}`).join(" ");
  const areaPts = `${toX(0)},${padTop + innerH} ${linePts} ${toX(values.length - 1)},${padTop + innerH}`;
  const yTicks = [0, maxV * 0.33, maxV * 0.67, maxV].map(t => Math.round(t / 1000) * 1000);

  // Callout rendered inside SVG at the active point
  const callout = activeIdx !== null ? (() => {
    const cash = values[activeIdx];
    const cx = toX(activeIdx);
    const cy = toY(cash);
    const delta = cash - baseline;
    const isBaseline = activeIdx === 0;
    const monthStr  = fmtMonthLabel(monthly[activeIdx].snapshotDate);
    const cashStr   = fmtDollars(cash);
    const deltaStr  = isBaseline
      ? "Baseline"
      : `${delta >= 0 ? "+" : ""}${fmtDollars(Math.abs(delta))} vs ${fmtMonthLabel(monthly[0].snapshotDate)}`;
    const deltaColor = isBaseline ? "#64748b" : delta >= 0 ? "#34d399" : "#f87171";
    const boxW = 148, boxH = 50;
    let boxX = cx - boxW / 2;
    boxX = Math.max(padLeft, Math.min(W - padRight - boxW, boxX));
    const boxY = Math.max(padTop + 2, cy - boxH - 12);
    return (
      <g style={{ pointerEvents: "none" }}>
        <line x1={cx} y1={cy - 6} x2={cx} y2={padTop + innerH}
          stroke="#38bdf8" strokeWidth="1" strokeDasharray="3,2" opacity="0.35" />
        <rect x={boxX} y={boxY} width={boxW} height={boxH} rx="4"
          fill="#0c1628" stroke="#1e3a5f" strokeWidth="1" />
        <text x={boxX + 9} y={boxY + 15} fontSize="9" fill="#38bdf8" fontWeight="700">
          {monthStr}
        </text>
        <text x={boxX + 9} y={boxY + 29} fontSize="9.5" fill="#e2e8f0"
          fontFamily="ui-monospace, monospace" fontWeight="600">
          {cashStr}
        </text>
        <text x={boxX + 9} y={boxY + 43} fontSize="8.5" fill={deltaColor}>
          {deltaStr}
        </text>
        <circle cx={cx} cy={cy} r="5" fill="#38bdf8" stroke="#0c1628" strokeWidth="2" />
      </g>
    );
  })() : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "240px" }}
      role="img" aria-label="Historical Cash Balance — hover each point for details"
      onMouseLeave={() => setActiveIdx(null)}>
      <defs>
        <linearGradient id="rtCashGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#38bdf8" stopOpacity="0.20" />
          <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {yTicks.map((t, i) => (
        <g key={i}>
          <line x1={padLeft} y1={toY(t)} x2={W - padRight} y2={toY(t)}
            stroke="rgba(148,163,184,0.07)" strokeWidth="1" />
          <text x={padLeft - 5} y={toY(t) + 3.5} textAnchor="end" fontSize="9" fill="#64748b">
            {t >= 1_000_000 ? `$${(t / 1_000_000).toFixed(1)}M`
              : t >= 1_000   ? `$${(t / 1_000).toFixed(0)}k`
              : "$0"}
          </text>
        </g>
      ))}
      <polygon points={areaPts} fill="url(#rtCashGrad)" />
      <polyline points={linePts} fill="none" stroke="#38bdf8" strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" />
      {callout}
      {monthly.map((m, i) => (
        <g key={i}>
          {activeIdx !== i && (
            <circle cx={toX(i)} cy={toY(values[i])} r="3" fill="#38bdf8"
              style={{ pointerEvents: "none" }} />
          )}
          <text x={toX(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="#64748b"
            style={{ pointerEvents: "none" }}>
            {fmtMonthLabel(m.snapshotDate)}
          </text>
          {/* Invisible hit area — triggers hover/click callout */}
          <circle cx={toX(i)} cy={toY(values[i])} r="16"
            fill="transparent"
            style={{ cursor: "pointer" }}
            onMouseEnter={() => setActiveIdx(i)}
            onClick={() => setActiveIdx(prev => prev === i ? null : i)}
          />
        </g>
      ))}
    </svg>
  );
}

/**
 * BarGroup — pure SVG grouped bars for one metric.
 * Self-scaled per metric (y-axis is independent for each call).
 * Each bar is labeled with its actual value. Mixed units handled by caller's `fmt`.
 */
function BarGroup({
  vals, labels, color, fmt,
}: {
  vals: (number | null)[];
  labels: string[];
  color: string;
  fmt: (v: number) => string;
}) {
  const W = 240, H = 110;
  const padTop = 28, padBottom = 26, padLeft = 4, padRight = 4;
  const innerW = W - padLeft - padRight;
  const innerH = H - padTop - padBottom;
  const nonNull = vals.filter((v): v is number => v != null);
  if (nonNull.length === 0) return null;
  const maxV = Math.max(...nonNull) * 1.22 || 1;
  const n = vals.length;
  const groupW = innerW / n;
  const barW = Math.min(groupW * 0.52, 38);
  // Opacity by position: Oct = dim, Feb = mid, May = full
  const opacities = [0.42, 0.68, 1.0];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "110px" }}
      role="img">
      {vals.map((v, i) => {
        if (v == null) return null;
        const barH = Math.max(2, (v / maxV) * innerH);
        const x = padLeft + groupW * i + (groupW - barW) / 2;
        const y = padTop + innerH - barH;
        const op = opacities[i] ?? 1.0;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH}
              fill={color} opacity={op} rx="2" />
            {/* Value label above bar */}
            <text x={x + barW / 2} y={y - 5} textAnchor="middle"
              fontSize="8.5" fill="#e2e8f0" fontFamily="ui-monospace, monospace" fontWeight="500">
              {fmt(v)}
            </text>
            {/* Month label below */}
            <text x={x + barW / 2} y={H - 6} textAnchor="middle"
              fontSize="8" fill="#64748b">
              {labels[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * KpiComparisonBars — full-width 2×2 grid comparing Oct '25, Feb '26, May '26
 * across Current Ratio, Gross Margin %, Debt/Equity, and LOC Available.
 * Each metric is self-scaled independently (no cross-metric normalization).
 * D/E treatment: bars are labeled normally; "lower is better" sub-label handles context.
 */
function KpiComparisonBars({ monthly }: { monthly: SnapshotEntry[] }) {
  // Fixed comparison points — dates selected from the deduplicated monthly series
  const oct25 = monthly.find(m => m.snapshotDate.startsWith("2025-10"));
  const feb26 = monthly.find(m => m.snapshotDate.startsWith("2026-02")); // → 2026-02-28
  const may26 = monthly[monthly.length - 1]; // latest (May '26)
  const points = [oct25, feb26, may26].filter((p): p is SnapshotEntry => !!p);
  const ptLabels = points.map(p => fmtMonthLabel(p.snapshotDate));

  type MetricDef = {
    key: keyof SnapshotEntry;
    label: string;
    subLabel?: string;
    color: string;
    fmt: (v: number) => string;
    /** When false, a declining value is an improvement. Used for delta signal only. */
    positiveIsGood: boolean;
  };
  const metrics: MetricDef[] = [
    { key: "currentRatio",      label: "Current Ratio",
      color: "#34d399", fmt: v => v.toFixed(2) + "×", positiveIsGood: true },
    { key: "grossMarginPctYtd", label: "Gross Margin %",
      color: "#38bdf8", fmt: v => v.toFixed(1) + "%",  positiveIsGood: true },
    { key: "debtToEquity",      label: "Debt / Equity",  subLabel: "(lower is better)",
      color: "#fbbf24", fmt: v => v.toFixed(2),          positiveIsGood: false },
    { key: "creditAvailable",   label: "LOC Available",
      color: "#a78bfa", fmt: v => `$${(v / 1000).toFixed(0)}k`, positiveIsGood: true },
  ];

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {metrics.map(m => {
        const vals = points.map(p => p[m.key] as number | null);
        const nonNull = vals.filter((v): v is number => v != null);
        if (nonNull.length === 0) return null;
        // Delta signal: first → last available value
        const firstV = nonNull[0];
        const lastV  = nonNull[nonNull.length - 1];
        const delta  = lastV - firstV;
        const effectiveDelta = m.positiveIsGood ? delta : -delta;
        const deltaColor = effectiveDelta >= 0 ? "text-emerald-400" : "text-rose-400";
        const deltaArrow = effectiveDelta >= 0 ? "▲" : "▼";
        const deltaText  = m.key === "creditAvailable"
          ? `${delta >= 0 ? "+" : ""}$${Math.round(Math.abs(delta) / 1000)}k`
          : m.key === "grossMarginPctYtd"
          ? `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}pp`
          : `${delta >= 0 ? "+" : ""}${Math.abs(delta) < 10 ? delta.toFixed(2) : delta.toFixed(1)}`;

        return (
          <div key={String(m.key)}
            className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
            <div className="flex items-baseline justify-between mb-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                {m.label}
              </span>
              <span className={`text-[9px] font-medium ${deltaColor}`}>
                {deltaArrow} {deltaText}
              </span>
            </div>
            {m.subLabel && (
              <div className="text-[9px] text-slate-500 mb-2">{m.subLabel}</div>
            )}
            {!m.subLabel && <div className="mb-2" />}
            <BarGroup vals={vals} labels={ptLabels} color={m.color} fmt={m.fmt} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * InfoTooltip — small inline info icon with hover tooltip.
 * Pure CSS/Tailwind, no library. Positions above the icon.
 */
function InfoTooltip({ text }: { text: string }) {
  return (
    <span className="relative group inline-block align-middle ml-1 cursor-help">
      {/* Info icon */}
      <span className="inline-flex items-center justify-center w-3 h-3 rounded-full border border-slate-600 text-slate-600 text-[8px] font-bold leading-none group-hover:border-slate-400 group-hover:text-slate-400 transition-colors select-none">
        i
      </span>
      {/* Tooltip bubble */}
      <span
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50
          w-44 rounded-md border border-slate-700 bg-slate-900 px-2.5 py-2
          text-[10px] leading-snug text-slate-300 shadow-xl
          opacity-0 group-hover:opacity-100 transition-opacity duration-150"
      >
        {text}
        {/* Caret */}
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-700" />
      </span>
    </span>
  );
}

// Approved tooltip copy — one sentence, plain English, no jargon
const READINESS_TOOLTIPS = {
  cashOnHand:     "Cash available to cover near-term needs like payroll, materials, and timing gaps.",
  workingCapital: "Current assets minus current liabilities, showing the operating cushion available to support active jobs.",
  grossMargin:    "The share of revenue left after direct job costs, helping show whether work has sufficient margin.",
  locAvailable:   "Unused line of credit capacity that can help bridge timing gaps between project costs and customer payments.",
  currentRatio:   "Current assets divided by current liabilities, showing short-term flexibility to meet obligations.",
  debtToEquity:   "Debt compared with owner equity, helping show whether the balance sheet can support sustainable growth.",
} as const;

function ReadinessPanel({
  data,
  loading,
  error,
}: {
  data: SnapshotEntry[] | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return <p className="text-xs text-slate-400">Loading balance sheet history…</p>;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 text-xs text-slate-400">
        <span className="font-semibold text-amber-300">Balance sheet history unavailable.</span>{" "}
        Other dashboard tabs are unaffected. Try refreshing the Readiness tab.
      </div>
    );
  }
  if (!data || data.length === 0) {
    return <p className="text-xs text-slate-400">No balance sheet snapshots available for this organization.</p>;
  }

  const monthly = toMonthlySeries(data);
  if (monthly.length === 0) {
    return <p className="text-xs text-slate-400">No balance sheet snapshots available for this organization.</p>;
  }

  const latest = monthly[monthly.length - 1];
  const first  = monthly[0];
  const latestLabel = fmtMonthLabel(latest.snapshotDate);

  const fmtRatio = (v: number | null) => v != null ? v.toFixed(2) : "—";
  const fmtPct   = (v: number | null) => v != null ? `${v.toFixed(1)}%` : "—";

  const cashTrend  = (latest.cashOnHand      ?? 0) - (first.cashOnHand      ?? 0);
  const ratioDelta = (latest.currentRatio    ?? 0) - (first.currentRatio    ?? 0);
  const deTrend    = (first.debtToEquity     ?? 0) - (latest.debtToEquity   ?? 0);

  // Signal dots — mirrors spec's mcard-signal
  type Sig = "green" | "amber" | "red";
  const sig = (s: Sig) => {
    const cls =
      s === "green" ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" :
      s === "amber" ? "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.4)]" :
                     "bg-rose-400 shadow-[0_0_6px_rgba(248,113,113,0.4)]";
    return <span className={`absolute top-2.5 right-2.5 inline-block w-1.5 h-1.5 rounded-full ${cls}`} />;
  };
  const cashSig: Sig = cashTrend > 0 ? "green" : "amber";
  const wcSig:   Sig = (latest.workingCapital   ?? 0) >= 400000 ? "green" : (latest.workingCapital   ?? 0) >= 150000 ? "amber" : "red";
  const gmSig:   Sig = (latest.grossMarginPctYtd ?? 0) >= 14   ? "green" : (latest.grossMarginPctYtd ?? 0) >= 11   ? "amber" : "red";
  const locSig:  Sig = (latest.creditAvailable  ?? 0) >= 200000 ? "green" : (latest.creditAvailable  ?? 0) >= 100000 ? "amber" : "red";
  const crSig:   Sig = (latest.currentRatio     ?? 0) >= 1.5   ? "green" : (latest.currentRatio     ?? 0) >= 1.0   ? "amber" : "red";

  return (
    <>
      <SectionTitle
        title="Readiness Timeline"
        subtitle={`Balance sheet trajectory through ${latestLabel} — ${monthly.length}-month historical series`}
      />

      {/* ── Hero KPI cards — mirrors spec's metrics-row ── */}
      <div className="grid gap-2 grid-cols-2 md:grid-cols-4 mb-4">

        <div className="relative rounded-lg border border-slate-800 bg-slate-950/50 px-3 pt-3 pb-2.5">
          {sig(cashSig)}
          <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-1.5">Cash on Hand<InfoTooltip text={READINESS_TOOLTIPS.cashOnHand} /></div>
          <div className="text-lg font-semibold font-mono text-slate-50 leading-none mb-1">
            {latest.cashOnHand != null ? fmtDollars(latest.cashOnHand) : "—"}
          </div>
          <div className={`text-[10px] font-medium ${cashTrend >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {cashTrend >= 0 ? "▲" : "▼"} {fmtDollars(Math.abs(cashTrend))} vs {fmtMonthLabel(first.snapshotDate)}
          </div>
        </div>

        <div className="relative rounded-lg border border-slate-800 bg-slate-950/50 px-3 pt-3 pb-2.5">
          {sig(wcSig)}
          <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-1.5">Working Capital<InfoTooltip text={READINESS_TOOLTIPS.workingCapital} /></div>
          <div className="text-lg font-semibold font-mono text-slate-50 leading-none mb-1">
            {latest.workingCapital != null ? fmtDollars(latest.workingCapital) : "—"}
          </div>
          <div className="text-[10px] text-slate-500">
            {latest.workingCapitalStatus === "calculated" ? "Current assets − liabilities" : "Balance sheet source pending"}
          </div>
        </div>

        <div className="relative rounded-lg border border-slate-800 bg-slate-950/50 px-3 pt-3 pb-2.5">
          {sig(gmSig)}
          <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-1.5">Gross Margin %<InfoTooltip text={READINESS_TOOLTIPS.grossMargin} /></div>
          <div className="text-lg font-semibold font-mono text-slate-50 leading-none mb-1">
            {fmtPct(latest.grossMarginPctYtd)}
          </div>
          {latest.grossMarginPctYtd != null && first.grossMarginPctYtd != null && (
            <div className={`text-[10px] font-medium ${latest.grossMarginPctYtd >= first.grossMarginPctYtd ? "text-emerald-400" : "text-rose-400"}`}>
              {latest.grossMarginPctYtd >= first.grossMarginPctYtd ? "▲" : "▼"}{" "}
              {Math.abs(latest.grossMarginPctYtd - first.grossMarginPctYtd).toFixed(1)}pp over period
            </div>
          )}
        </div>

        <div className="relative rounded-lg border border-slate-800 bg-slate-950/50 px-3 pt-3 pb-2.5">
          {sig(locSig)}
          <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-1.5">LOC Available<InfoTooltip text={READINESS_TOOLTIPS.locAvailable} /></div>
          <div className="text-lg font-semibold font-mono text-slate-50 leading-none mb-1">
            {latest.creditAvailable != null ? fmtDollars(latest.creditAvailable) : "—"}
          </div>
          <div className={`text-[10px] font-medium ${(latest.creditAvailable ?? 0) > (first.creditAvailable ?? 0) ? "text-emerald-400" : "text-slate-500"}`}>
            {(latest.creditAvailable ?? 0) > (first.creditAvailable ?? 0)
              ? `▲ ${fmtDollars((latest.creditAvailable ?? 0) - (first.creditAvailable ?? 0))} headroom gained`
              : "Line of credit headroom"}
          </div>
        </div>
      </div>

      {/* ── Historical Cash Balance — full width with hover callout ── */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 mb-4">
        <div className="flex items-baseline justify-between mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Historical Cash Balance
          </span>
          <span className="text-[9px] text-slate-600">Hover each point for details</span>
        </div>
        <HistoricalCashChart monthly={monthly} />
      </div>

      {/* ── KPI Comparison by Month — full width, Oct/Feb/May bar groups ── */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 mb-4">
        <div className="flex items-baseline justify-between mb-4">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            KPI Comparison by Month
          </span>
          <span className="text-[9px] text-slate-600">
            {fmtMonthLabel(monthly[0]?.snapshotDate ?? "")} · Feb '26 · {fmtMonthLabel(monthly[monthly.length - 1]?.snapshotDate ?? "")}
          </span>
        </div>
        <KpiComparisonBars monthly={monthly} />
      </div>

      {/* ── Bottom row — 1fr:1fr mirrors spec's inputs-row ── */}
      <div className="grid gap-3 md:grid-cols-2 mb-4">

        {/* Left — Readiness Actions (= Model Inputs) */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Readiness Actions</span>
            <span className="text-[10px] text-slate-600">Based on {latestLabel} snapshot</span>
          </div>
          {/* Supporting ratio mini-cards */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="relative rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2">
              {sig(crSig)}
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 mb-0.5">Current Ratio<InfoTooltip text={READINESS_TOOLTIPS.currentRatio} /></div>
              <div className="text-sm font-semibold font-mono text-slate-50">{fmtRatio(latest.currentRatio)}</div>
              {ratioDelta > 0 && <div className="text-[9px] text-emerald-400 mt-0.5">Leverage position improved</div>}
            </div>
            <div className="relative rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2">
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 mb-0.5">Debt / Equity<InfoTooltip text={READINESS_TOOLTIPS.debtToEquity} /></div>
              <div className="text-sm font-semibold font-mono text-slate-50">{fmtRatio(latest.debtToEquity)}</div>
              {deTrend > 0 && latest.debtToEquityStatus === "calculated" && (
                <div className="text-[9px] text-emerald-400 mt-0.5">Sustainability strengthened</div>
              )}
            </div>
          </div>
          {/* Action items */}
          <div className="space-y-2">
            {cashTrend > 0 && (
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-emerald-400 text-[10px] mt-0.5 shrink-0">●</span>
                <span className="text-slate-300">Cash position strengthened by {fmtDollars(cashTrend)} over the period — a more comfortable profile for larger project conversations.</span>
              </div>
            )}
            {(latest.currentRatio ?? 0) >= 1.5 && (
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-emerald-400 text-[10px] mt-0.5 shrink-0">●</span>
                <span className="text-slate-300">Current ratio of {fmtRatio(latest.currentRatio)} reflects a healthy liquidity cushion for GC and bonding conversations.</span>
              </div>
            )}
            {deTrend > 0 && latest.debtToEquityStatus === "calculated" && (
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-emerald-400 text-[10px] mt-0.5 shrink-0">●</span>
                <span className="text-slate-300">Debt-to-equity improved from {fmtRatio(first.debtToEquity)} → {fmtRatio(latest.debtToEquity)} — balance sheet sustainability strengthened over the period.</span>
              </div>
            )}
            {(latest.currentRatio ?? 0) < 1.5 && latest.currentRatioStatus === "calculated" && (
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-amber-400 text-[10px] mt-0.5 shrink-0">→</span>
                <span className="text-slate-300">Continuing to grow working capital will further strengthen the profile ahead of larger project bids.</span>
              </div>
            )}
            <div className="flex items-start gap-2 text-[11px]">
              <span className="text-sky-400 text-[10px] mt-0.5 shrink-0">→</span>
              <span className="text-slate-300">Connect updated financial statements to keep the Readiness Timeline current for GC and bonding conversations.</span>
            </div>
          </div>
        </div>

        {/* Right — Snapshot History (= Anchored to Actuals) */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Snapshot History</span>
            <span className="text-[10px] text-slate-600">Read-only · from balance sheet</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[10.5px]">
              <thead>
                <tr className="border-b border-slate-800 text-slate-500">
                  <th className="py-1.5 text-left pr-2 font-medium">Month</th>
                  <th className="py-1.5 text-right pr-2 font-medium">Cash</th>
                  <th className="py-1.5 text-right pr-2 font-medium">Wk Cap</th>
                  <th className="py-1.5 text-right pr-2 font-medium">GM%</th>
                  <th className="py-1.5 text-right font-medium">D/E</th>
                </tr>
              </thead>
              <tbody>
                {[...monthly].reverse().map((s, i) => {
                  const isLatest = i === 0;
                  return (
                    <tr key={s.snapshotDate}
                      className={`border-b border-slate-900/80 ${isLatest ? "bg-sky-900/10" : ""}`}>
                      <td className={`py-1.5 pr-2 font-medium ${isLatest ? "text-sky-300" : "text-slate-300"}`}>
                        {fmtMonthLabel(s.snapshotDate)}
                      </td>
                      <td className="py-1.5 text-right pr-2 font-mono text-slate-200">
                        {s.cashOnHand != null ? fmtDollars(s.cashOnHand) : "—"}
                      </td>
                      <td className="py-1.5 text-right pr-2 font-mono text-slate-200">
                        {s.workingCapital != null ? fmtDollars(s.workingCapital) : "—"}
                      </td>
                      <td className="py-1.5 text-right pr-2 font-mono text-slate-200">
                        {s.grossMarginPctYtd != null ? `${s.grossMarginPctYtd.toFixed(1)}%` : "—"}
                      </td>
                      <td className="py-1.5 text-right font-mono text-slate-200">
                        {s.debtToEquity != null ? s.debtToEquity.toFixed(2) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[9.5px] text-slate-600">
            Latest snapshot per calendar month · Source: balance sheet statements
          </p>
        </div>
      </div>

      {/* ── Advanced modeling: Scenario Modeling is live (see sidebar); Capital & Bonding Brief is not yet built. ── */}
      <div className="mb-1.5">
        <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-600">Advanced Modeling</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <a href="/cfo-insights/scenarios"
          className="rounded-xl border border-sky-700/40 bg-sky-900/10 p-4 block hover:border-sky-500/60 transition-colors">
          <div className="text-xs font-semibold text-sky-300 mb-1">Scenario Modeling</div>
          <div className="text-[11px] text-slate-400">Model the impact of adding a specific project across Base, Growth, and Stress cases.</div>
        </a>
        <div className="rounded-xl border border-slate-700/40 bg-slate-900/20 p-4 select-none"
          style={{ cursor: "not-allowed", opacity: 0.48 }} aria-disabled="true">
          <div className="text-xs font-semibold text-slate-400 mb-1">Capital &amp; Bonding Brief</div>
          <div className="text-[11px] text-slate-600">Structured summary for bonding conversations. Coming soon.</div>
        </div>
      </div>
    </>
  );
}

function MetricCard(props: {
  label: string;
  value: string;
  tone?: "good" | "warning" | "bad" | "neutral";
  hint?: string;
}) {
  const tone = props.tone ?? "neutral";
  const pillClass =
    tone === "good"    ? "bg-emerald-500/10 text-emerald-300" :
    tone === "warning" ? "bg-amber-500/10   text-amber-300"   :
    tone === "bad"     ? "bg-rose-500/10    text-rose-300"    :
                         "bg-slate-700/40   text-slate-200";
  const pillLabel =
    tone === "good" ? "Healthy" : tone === "warning" ? "Monitor" : tone === "bad" ? "Attention" : "Info";

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-300">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{props.label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-50">{props.value}</div>
      <div className="mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium">
        <span className={pillClass}>{pillLabel}</span>
      </div>
      {props.hint && <p className="mt-1 text-[11px] text-slate-400">{props.hint}</p>}
    </div>
  );
}
